/**
 * Push subscription helper. Registers the service worker (once),
 * asks the browser for permission (only when the caller decides the
 * moment is right), and POSTs the subscription to the backend.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function pushPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function ensureServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/sw.js');
    if (existing) return existing;
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

export async function requestAndSubscribe() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (!VAPID_PUBLIC_KEY) return { ok: false, reason: 'missing_vapid' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };
  try {
    const reg = await ensureServiceWorker();
    if (!reg) return { ok: false, reason: 'no_sw' };
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    await axios.post(`${API}/push/subscribe`, sub.toJSON());
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || 'subscribe_failed' };
  }
}

/**
 * Anonymous subscribe for guests. Keyed by anon_id + optional event_id.
 * Caller is expected to pass a stable anon_id from localStorage (the
 * same one used for growthTrack).
 */
export async function requestAndSubscribeGuest({ anon_id, event_id } = {}) {
  if (!anon_id) return { ok: false, reason: 'missing_anon_id' };
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (!VAPID_PUBLIC_KEY) return { ok: false, reason: 'missing_vapid' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };
  try {
    const reg = await ensureServiceWorker();
    if (!reg) return { ok: false, reason: 'no_sw' };
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    await axios.post(`${API}/push/subscribe-guest`, {
      ...sub.toJSON(),
      anon_id,
      event_id,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || 'subscribe_failed' };
  }
}

export function ackPushClick(psid) {
  if (!psid) return;
  axios.post(`${API}/push/ack`, { send_id: psid, action: 'clicked' }).catch(() => {});
}
